import React, { useState, useEffect, useRef } from 'react';
import { payrollAPI, clientSettingsAPI } from '../../services';
import {
  Button,
  Card,
  Modal,
  Input,
  Select,
  Badge,
  LoadingSpinner,
  ErrorMessage,
  SuccessMessage,
} from '../UI';
import { 
  Upload, 
  FileText, 
  Download, 
  Trash2, 
  Eye,
  Calendar,
  Users,
  DollarSign,
  Filter,
  RefreshCw,
  CheckCircle2,
  Clock,
  AlertCircle,
  Search,
  FilePlus,
  Printer,
  FileSpreadsheet,
  Mail
} from 'lucide-react';
import { generatePayslipPDF, printPayslipPDF } from './PayslipPDFWrapper';

const EmployeePayrollManagement = () => {
  const [activeSubTab, setActiveSubTab] = useState('payslips');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Data states
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [clientData, setClientData] = useState(null);

  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(null); // Stores payslip ID being emailed

  // Export to Excel state
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMonth, setExportMonth] = useState(new Date().getMonth() === 0 ? 12 : new Date().getMonth());
  const [exportYear, setExportYear] = useState(new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear());
  const [exportType, setExportType] = useState('payroll'); // 'payroll' or 'attendance'

  // Generate payslip form states
  const [generateForm, setGenerateForm] = useState({
    employeeId: '',
    month: new Date().getMonth() === 0 ? 12 : new Date().getMonth(),
    year: new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear(),
    bonus: 0,
    incentive: 0,
    deductions: {
      pf: 0,
      esi: 0,
      tax: 0,
      lop: 0,
      advance: 0,
      other: 0
    }
  });
  const [generating, setGenerating] = useState(false);

  // Bulk generate and email states
  const [bulkForm, setBulkForm] = useState({
    month: new Date().getMonth() === 0 ? 12 : new Date().getMonth(),
    year: new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear()
  });
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  // Upload form states
  const [uploadForm, setUploadForm] = useState({
    employeeId: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    netPayable: '',
    notes: ''
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Filter states
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    employeeId: '',
    status: ''
  });

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  useEffect(() => {
    fetchEmployees();
    fetchClientData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeSubTab, filters]);

  const fetchClientData = async () => {
    try {
      const response = await clientSettingsAPI.getMySettings();
      setClientData(response.data);
    } catch (err) {
      console.log('Could not fetch client data:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await payrollAPI.getEmployeesForPayroll();
      setEmployees(response.data || []);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      if (activeSubTab === 'payslips') {
        const response = await payrollAPI.getAllPayslips({
          month: filters.month || undefined,
          year: filters.year,
          employeeId: filters.employeeId || undefined,
          status: filters.status || undefined
        });
        setPayslips(response.data?.payslips || []);
      } else if (activeSubTab === 'stats') {
        const response = await payrollAPI.getPayrollStats({ year: filters.year });
        setStats(response.data || null);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Only PDF files are allowed');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setUploadFile(file);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.employeeId) {
      setError('Please select an employee');
      return;
    }

    if (!uploadFile) {
      setError('Please select a PDF file to upload');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('document', uploadFile);
      formData.append('data', JSON.stringify({
        employeeId: uploadForm.employeeId,
        month: uploadForm.month,
        year: uploadForm.year,
        netPayable: uploadForm.netPayable || 0,
        notes: uploadForm.notes
      }));

      await payrollAPI.uploadPayslip(formData);
      
      setSuccess('Payslip uploaded successfully');
      setShowUploadModal(false);
      resetUploadForm();
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to upload payslip');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      employeeId: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      netPayable: '',
      notes: ''
    });
    setUploadFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Generate payslip from salary configuration
  const handleGeneratePayslip = async () => {
    if (!generateForm.employeeId) {
      setError('Please select an employee');
      return;
    }

    // Prevent generating payslip for current or future months
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    if (generateForm.year > currentYear || (generateForm.year === currentYear && generateForm.month >= currentMonth)) {
      setError('Payslips can only be generated for past months. Current and future month payslips are not allowed.');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      await payrollAPI.generatePayslip({
        employeeId: generateForm.employeeId,
        month: generateForm.month,
        year: generateForm.year,
        bonus: generateForm.bonus || 0,
        incentive: generateForm.incentive || 0,
        deductions: generateForm.deductions
      });
      
      setSuccess('Payslip generated successfully');
      setShowGenerateModal(false);
      setGenerateForm({
        employeeId: '',
        month: new Date().getMonth() === 0 ? 12 : new Date().getMonth(),
        year: new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear(),
        bonus: 0,
        incentive: 0,
        deductions: {
          pf: 0,
          esi: 0,
          tax: 0,
          lop: 0,
          advance: 0,
          other: 0
        }
      });
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to generate payslip');
    } finally {
      setGenerating(false);
    }
  };

  // Handle export to Excel
  const handleExportToExcel = async () => {
    setExportLoading(true);
    setError('');
    try {
      if (exportType === 'attendance') {
        await payrollAPI.exportAttendanceToExcel(exportMonth, exportYear);
        setSuccess('Attendance Excel file downloaded successfully!');
      } else {
        await payrollAPI.exportToExcel(exportMonth, exportYear);
        setSuccess('Payroll Excel file downloaded successfully!');
      }
      setShowExportModal(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to export to Excel');
    } finally {
      setExportLoading(false);
    }
  };

  // Bulk generate payslips and email to all employees
  const handleBulkGenerateAndEmail = async () => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    if (bulkForm.year > currentYear || (bulkForm.year === currentYear && bulkForm.month >= currentMonth)) {
      setError('Payslips can only be generated for past months. Current and future month payslips are not allowed.');
      return;
    }

    setBulkGenerating(true);
    setBulkResults(null);
    setError('');

    try {
      const response = await payrollAPI.generateBulkAndEmail({
        month: bulkForm.month,
        year: bulkForm.year
      });
      
      setBulkResults(response.data);
      setSuccess(response.message || 'Bulk payslips generated and emailed successfully!');
      fetchData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || 'Failed to generate bulk payslips');
    } finally {
      setBulkGenerating(false);
    }
  };

  // Download PDF for a payslip
  const handleDownloadPDF = async (payslip) => {
    setPdfLoading(true);
    setError('');
    
    try {
      // Ensure client data is loaded
      let localClientData = clientData;
      if (!localClientData) {
        try {
          const clientResponse = await clientSettingsAPI.getMySettings();
          localClientData = clientResponse.data;
          setClientData(localClientData);
        } catch (clientErr) {
          console.log('Could not fetch client data:', clientErr);
        }
      }
      
      // Get employee details - prioritize from employees list, then payslip.employee
      const employeeFromList = employees.find(e => e._id === (payslip.employee?._id || payslip.employee));
      // Merge data to ensure we have all fields
      const employee = employeeFromList ? {
        ...payslip.employee,
        ...employeeFromList,
      } : payslip.employee;
      
      const result = await generatePayslipPDF(
        payslip,
        employee,
        localClientData
      );
      
      if (result.success) {
        setSuccess(`PDF downloaded: ${result.fileName}`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to generate PDF: ' + result.error);
      }
    } catch (err) {
      setError('Failed to generate PDF: ' + err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  // Print PDF for a payslip
  const handlePrintPDF = async (payslip) => {
    setPdfLoading(true);
    setError('');
    
    try {
      // Ensure client data is loaded
      let localClientData = clientData;
      if (!localClientData) {
        try {
          const clientResponse = await clientSettingsAPI.getMySettings();
          localClientData = clientResponse.data;
          setClientData(localClientData);
        } catch (clientErr) {
          console.log('Could not fetch client data:', clientErr);
        }
      }
      
      // Get employee details - prioritize from employees list, then payslip.employee
      const employeeFromList = employees.find(e => e._id === (payslip.employee?._id || payslip.employee));
      const employee = employeeFromList ? {
        ...payslip.employee,
        ...employeeFromList,
      } : payslip.employee;
      
      const result = await printPayslipPDF(
        payslip,
        employee,
        localClientData
      );
      
      if (!result.success) {
        setError('Failed to print PDF: ' + result.error);
      }
    } catch (err) {
      setError('Failed to print PDF: ' + err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPayslip) return;
    try {
      await payrollAPI.deletePayslip(selectedPayslip._id);
      setSuccess('Payslip deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedPayslip(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete payslip');
    }
  };

  const handleDownload = (payslip) => {
    if (payslip.document?.url) {
      window.open(payslip.document.url, '_blank');
    }
  };

  // Send payslip via email
  const handleSendEmail = async (payslip) => {
    setEmailLoading(payslip._id);
    setError('');
    try {
      const response = await payrollAPI.emailPayslip(payslip._id);
      if (response.success) {
        setSuccess(`Payslip emailed successfully to ${payslip.employee?.email || 'employee'}`);
        setTimeout(() => setSuccess(''), 4000);
      } else {
        setError(response.message || 'Failed to send email');
      }
    } catch (err) {
      setError(err.message || 'Failed to send payslip email');
    } finally {
      setEmailLoading(null);
    }
  };

  const openDetailModal = (payslip) => {
    setSelectedPayslip(payslip);
    setShowDetailModal(true);
  };

  const openDeleteConfirm = (payslip) => {
    setSelectedPayslip(payslip);
    setShowDeleteConfirm(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800 border-gray-300',
      generated: 'bg-blue-100 text-blue-800 border-blue-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      paid: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      cancelled: 'bg-red-100 text-red-800 border-red-300'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getMonthName = (month) => {
    return months.find(m => m.value === month)?.label || '';
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-4">
        {[
          { id: 'payslips', label: 'Payslips', icon: FileText },
          { id: 'stats', label: 'Statistics', icon: DollarSign }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeSubTab === tab.id
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Messages */}
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      {/* Actions */}
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Filters */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
          </div>
          <Select
            value={filters.month}
            onChange={(e) => setFilters({ ...filters, month: parseInt(e.target.value) || '' })}
            className="w-36"
          >
            <option value="">All Months</option>
            {months.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </Select>
          <Select
            value={filters.year}
            onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) })}
            className="w-28"
          >
            {[2024, 2025, 2026, 2027].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </Select>
          <Select
            value={filters.employeeId}
            onChange={(e) => setFilters({ ...filters, employeeId: e.target.value })}
            className="w-48"
          >
            <option value="">All Employees</option>
            {employees.map((emp) => (
              <option key={emp._id} value={emp._id}>{emp.name}</option>
            ))}
          </Select>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="secondary" onClick={() => setShowGenerateModal(true)}>
            <FilePlus className="w-4 h-4 mr-2" />
            Generate Payslip
          </Button>
          <Button onClick={() => setShowBulkModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Users className="w-4 h-4 mr-2" />
            Bulk Generate & Email
          </Button>
          <Button onClick={() => setShowUploadModal(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Upload Payslip
          </Button>
          <Button variant="outline" onClick={() => setShowExportModal(true)} className="bg-green-600 hover:bg-green-700 text-white border-green-600">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          {/* Payslips Tab */}
          {activeSubTab === 'payslips' && (
            <div className="space-y-4">
              {payslips.length === 0 ? (
                <Card>
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p className="text-lg font-medium">No payslips found</p>
                    <p className="text-sm">Generate or upload a payslip to get started.</p>
                    <div className="flex gap-3 justify-center mt-4">
                      <Button variant="secondary" onClick={() => setShowGenerateModal(true)}>
                        <FilePlus className="w-4 h-4 mr-2" />
                        Generate Payslip
                      </Button>
                      <Button onClick={() => setShowUploadModal(true)}>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Payslip
                      </Button>
                    </div>
                  </div>
                </Card>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 bg-white rounded-lg shadow">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Net Pay</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {payslips.map((payslip) => (
                        <tr key={payslip._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                <span className="text-indigo-600 text-sm font-medium">
                                  {payslip.employee?.name?.charAt(0) || '?'}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{payslip.employee?.name}</p>
                                <p className="text-xs text-gray-500">{payslip.employee?.department}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-900">
                              {getMonthName(payslip.month)} {payslip.year}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(payslip.netPayable)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {payslip.document?.url ? (
                              <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                                <CheckCircle2 className="w-4 h-4" />
                                Uploaded
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-gray-400 text-sm">
                                <Clock className="w-4 h-4" />
                                Not uploaded
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">{getStatusBadge(payslip.status)}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openDetailModal(payslip)}
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadPDF(payslip)}
                                disabled={pdfLoading}
                                title="Download PDF"
                                className="text-green-600 hover:text-green-700"
                              >
                                <FileText className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSendEmail(payslip)}
                                disabled={emailLoading === payslip._id}
                                title="Email Payslip"
                                className="text-blue-600 hover:text-blue-700"
                              >
                                {emailLoading === payslip._id ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4" />
                                )}
                              </Button>
                              {payslip.document?.url && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDownload(payslip)}
                                  title="Download Uploaded"
                                  className="text-blue-600 hover:text-blue-700"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                              )}
                              {['draft', 'generated'].includes(payslip.status) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openDeleteConfirm(payslip)}
                                  title="Delete"
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Statistics Tab */}
          {activeSubTab === 'stats' && stats && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <div className="p-4 text-center">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                    <p className="text-2xl font-bold text-blue-700">{stats.totalPayslips || 0}</p>
                    <p className="text-sm text-blue-600">Total Payslips</p>
                  </div>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <div className="p-4 text-center">
                    <DollarSign className="w-8 h-8 mx-auto mb-2 text-green-600" />
                    <p className="text-2xl font-bold text-green-700">{formatCurrency(stats.totalPaid || 0)}</p>
                    <p className="text-sm text-green-600">Total Paid</p>
                  </div>
                </Card>
                <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
                  <div className="p-4 text-center">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-yellow-600" />
                    <p className="text-2xl font-bold text-yellow-700">{stats.pending || 0}</p>
                    <p className="text-sm text-yellow-600">Pending</p>
                  </div>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <div className="p-4 text-center">
                    <Users className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                    <p className="text-2xl font-bold text-purple-700">{stats.employeesCount || 0}</p>
                    <p className="text-sm text-purple-600">Employees</p>
                  </div>
                </Card>
              </div>

              {stats.monthlyBreakdown && stats.monthlyBreakdown.length > 0 && (
                <Card title="Monthly Breakdown">
                  <div className="p-4">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Payslips</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {stats.monthlyBreakdown.map((item) => (
                            <tr key={`${item.month}-${item.year}`}>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {getMonthName(item.month)} {item.year}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-gray-600">{item.count}</td>
                              <td className="px-4 py-2 text-sm text-right font-medium text-gray-900">
                                {formatCurrency(item.total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}
        </>
      )}

      {/* Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          resetUploadForm();
        }}
        title="Upload Payslip"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee <span className="text-red-500">*</span>
            </label>
            <Select
              value={uploadForm.employeeId}
              onChange={(e) => setUploadForm({ ...uploadForm, employeeId: e.target.value })}
              className="w-full"
            >
              <option value="">Select Employee</option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.name} - {emp.department || 'N/A'}
                </option>
              ))}
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <Select
                value={uploadForm.month}
                onChange={(e) => setUploadForm({ ...uploadForm, month: parseInt(e.target.value) })}
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <Select
                value={uploadForm.year}
                onChange={(e) => setUploadForm({ ...uploadForm, year: parseInt(e.target.value) })}
              >
                {[2024, 2025, 2026, 2027].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Net Payable (₹)</label>
            <Input
              type="number"
              value={uploadForm.netPayable}
              onChange={(e) => setUploadForm({ ...uploadForm, netPayable: e.target.value })}
              placeholder="Enter net payable amount"
              min={0}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payslip PDF <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-indigo-400 transition-colors">
              <div className="space-y-1 text-center">
                {uploadFile ? (
                  <>
                    <FileText className="mx-auto h-12 w-12 text-green-500" />
                    <p className="text-sm text-gray-900 font-medium">{uploadFile.name}</p>
                    <p className="text-xs text-gray-500">
                      {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setUploadFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                    >
                      Remove
                    </Button>
                  </>
                ) : (
                  <>
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500">
                        <span>Upload a file</span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="sr-only"
                          accept="application/pdf"
                          onChange={handleFileChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PDF up to 10MB</p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={uploadForm.notes}
              onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows={2}
              placeholder="Optional notes..."
            />
          </div>

          <Button
            className="w-full"
            onClick={handleUpload}
            disabled={uploading || !uploadForm.employeeId || !uploadFile}
          >
            {uploading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload Payslip
              </>
            )}
          </Button>
        </div>
      </Modal>

      {/* Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Payslip Details"
      >
        {selectedPayslip && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-500">Status</span>
                {getStatusBadge(selectedPayslip.status)}
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Employee</span>
                <span className="font-medium">{selectedPayslip.employee?.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Department</span>
                <span className="text-sm">{selectedPayslip.employee?.department || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Pay Period</span>
                <span className="font-medium">
                  {getMonthName(selectedPayslip.month)} {selectedPayslip.year}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Net Payable</span>
                <span className="text-lg font-bold text-green-600">
                  {formatCurrency(selectedPayslip.netPayable)}
                </span>
              </div>
              {selectedPayslip.notes && (
                <div className="border-t pt-3">
                  <span className="text-sm text-gray-500">Notes</span>
                  <p className="mt-1 text-sm text-gray-900">{selectedPayslip.notes}</p>
                </div>
              )}
              {selectedPayslip.document && (
                <div className="border-t pt-3">
                  <span className="text-sm text-gray-500">Document</span>
                  <div className="mt-2 flex items-center justify-between bg-white p-3 rounded border">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-red-500" />
                      <span className="text-sm text-gray-900">{selectedPayslip.document.fileName}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(selectedPayslip)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              )}
              {selectedPayslip.generatedBy && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Generated By</span>
                  <span className="text-sm">{selectedPayslip.generatedBy?.name}</span>
                </div>
              )}
            </div>

            {/* PDF Download, Email and Print Buttons */}
            <div className="flex gap-3">
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700 text-white" 
                onClick={() => handleDownloadPDF(selectedPayslip)}
                disabled={pdfLoading}
              >
                {pdfLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                {pdfLoading ? 'Generating...' : 'Download PDF'}
              </Button>
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => handleSendEmail(selectedPayslip)}
                disabled={emailLoading === selectedPayslip?._id}
              >
                {emailLoading === selectedPayslip?._id ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                {emailLoading === selectedPayslip?._id ? 'Sending...' : 'Email'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => handlePrintPDF(selectedPayslip)}
                disabled={pdfLoading}
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>

            <Button variant="outline" className="w-full" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Payslip"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">
              Are you sure you want to delete this payslip for{' '}
              <strong>{selectedPayslip?.employee?.name}</strong> ({getMonthName(selectedPayslip?.month)} {selectedPayslip?.year})?
              This action cannot be undone.
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Generate Payslip Modal */}
      <Modal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        title="Generate Payslip"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Generate a payslip from the employee's salary configuration. The system will calculate earnings based on attendance and deductions.
          </p>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
            <Select
              value={generateForm.employeeId}
              onChange={(e) => setGenerateForm({ ...generateForm, employeeId: e.target.value })}
              className="w-full"
            >
              <option value="">Select Employee</option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>{emp.name}</option>
              ))}
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <Select
                value={generateForm.month}
                onChange={(e) => setGenerateForm({ ...generateForm, month: parseInt(e.target.value) })}
                className="w-full"
              >
                {months.map((m) => {
                  const isDisabled = generateForm.year > new Date().getFullYear() || (generateForm.year === new Date().getFullYear() && m.value >= new Date().getMonth() + 1);
                  return (
                    <option key={m.value} value={m.value} disabled={isDisabled}>
                      {m.label}{isDisabled ? ' (Not available)' : ''}
                    </option>
                  );
                })}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <Select
                value={generateForm.year}
                onChange={(e) => setGenerateForm({ ...generateForm, year: parseInt(e.target.value) })}
                className="w-full"
              >
                {[2024, 2025, 2026, 2027].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bonus (₹)</label>
              <Input
                type="number"
                value={generateForm.bonus}
                onChange={(e) => setGenerateForm({ ...generateForm, bonus: parseFloat(e.target.value) || 0 })}
                placeholder="0"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Incentive (₹)</label>
              <Input
                type="number"
                value={generateForm.incentive}
                onChange={(e) => setGenerateForm({ ...generateForm, incentive: parseFloat(e.target.value) || 0 })}
                placeholder="0"
                min="0"
              />
            </div>
          </div>

          {/* Deductions Section */}
          <div className="border-t pt-4 mt-2">
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <span className="text-red-600">Deductions</span>
              <span className="text-xs text-gray-500 font-normal">(Optional - Override auto-calculated values)</span>
            </h4>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">EPF (₹)</label>
                <Input
                  type="number"
                  value={generateForm.deductions.pf}
                  onChange={(e) => setGenerateForm({ 
                    ...generateForm, 
                    deductions: { ...generateForm.deductions, pf: parseFloat(e.target.value) || 0 }
                  })}
                  placeholder="0"
                  min="0"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">ESI (₹)</label>
                <Input
                  type="number"
                  value={generateForm.deductions.esi}
                  onChange={(e) => setGenerateForm({ 
                    ...generateForm, 
                    deductions: { ...generateForm.deductions, esi: parseFloat(e.target.value) || 0 }
                  })}
                  placeholder="0"
                  min="0"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Prof. Tax (₹)</label>
                <Input
                  type="number"
                  value={generateForm.deductions.tax}
                  onChange={(e) => setGenerateForm({ 
                    ...generateForm, 
                    deductions: { ...generateForm.deductions, tax: parseFloat(e.target.value) || 0 }
                  })}
                  placeholder="0"
                  min="0"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">LOP Deduction (₹)</label>
                <Input
                  type="number"
                  value={generateForm.deductions.lop}
                  onChange={(e) => setGenerateForm({ 
                    ...generateForm, 
                    deductions: { ...generateForm.deductions, lop: parseFloat(e.target.value) || 0 }
                  })}
                  placeholder="0"
                  min="0"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Advance (₹)</label>
                <Input
                  type="number"
                  value={generateForm.deductions.advance}
                  onChange={(e) => setGenerateForm({ 
                    ...generateForm, 
                    deductions: { ...generateForm.deductions, advance: parseFloat(e.target.value) || 0 }
                  })}
                  placeholder="0"
                  min="0"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Other (₹)</label>
                <Input
                  type="number"
                  value={generateForm.deductions.other}
                  onChange={(e) => setGenerateForm({ 
                    ...generateForm, 
                    deductions: { ...generateForm.deductions, other: parseFloat(e.target.value) || 0 }
                  })}
                  placeholder="0"
                  min="0"
                  className="text-sm"
                />
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> The employee must have a salary configuration set up. 
              Earnings will be calculated based on Basic, HRA, Allowances from their profile.
              Deductions are optional and will override auto-calculated values if provided.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowGenerateModal(false)}
              disabled={generating}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handleGeneratePayslip}
              disabled={generating || !generateForm.employeeId}
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FilePlus className="w-4 h-4 mr-2" />
                  Generate Payslip
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Export to Excel Modal */}
      <Modal isOpen={showExportModal} onClose={() => setShowExportModal(false)} size="md">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg">
              <FileSpreadsheet className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Export to Excel</h2>
              <p className="text-sm text-gray-500">Download attendance or payroll data</p>
            </div>
          </div>

          {/* Export Type Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Export Type</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  exportType === 'payroll' 
                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setExportType('payroll')}
              >
                <div className="font-medium">Payroll Report</div>
                <div className="text-xs text-gray-500 mt-1">Attendance + Salary + Deductions</div>
              </button>
              <button
                type="button"
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  exportType === 'attendance' 
                    ? 'border-green-500 bg-green-50 text-green-700' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setExportType('attendance')}
              >
                <div className="font-medium">Attendance Only</div>
                <div className="text-xs text-gray-500 mt-1">Daily attendance (1st-31st)</div>
              </button>
            </div>
          </div>

          <div className={`border rounded-lg p-3 mb-4 ${
            exportType === 'payroll' ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'
          }`}>
            <p className={`text-sm ${exportType === 'payroll' ? 'text-blue-800' : 'text-green-800'}`}>
              {exportType === 'payroll' 
                ? 'Export complete payroll data including daily attendance (pay period dates), earnings, deductions, and net pay for all employees.'
                : 'Export attendance report with daily status codes (P, A, CL, SL, W/O, H) for the calendar month (1st to last day).'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <Select
                value={exportMonth}
                onChange={(e) => setExportMonth(parseInt(e.target.value))}
                className="w-full"
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <Select
                value={exportYear}
                onChange={(e) => setExportYear(parseInt(e.target.value))}
                className="w-full"
              >
                {[2024, 2025, 2026, 2027].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Select>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowExportModal(false)}
              disabled={exportLoading}
            >
              Cancel
            </Button>
            <Button
              className={`flex-1 ${exportType === 'payroll' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}
              onClick={handleExportToExcel}
              disabled={exportLoading}
            >
              {exportLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export {exportType === 'payroll' ? 'Payroll' : 'Attendance'}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Generate & Email Modal */}
      <Modal isOpen={showBulkModal} onClose={() => { setShowBulkModal(false); setBulkResults(null); }} size="lg">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Bulk Generate & Email Payslips</h2>
              <p className="text-sm text-gray-500">Generate payslips for all employees and send via email</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-800">
              This will generate payslips for <strong>all active employees</strong> with configured salaries 
              and automatically email the PDF payslip to each employee's registered email address.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <Select
                value={bulkForm.month}
                onChange={(e) => setBulkForm({ ...bulkForm, month: parseInt(e.target.value) })}
                className="w-full"
                disabled={bulkGenerating}
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <Select
                value={bulkForm.year}
                onChange={(e) => setBulkForm({ ...bulkForm, year: parseInt(e.target.value) })}
                className="w-full"
                disabled={bulkGenerating}
              >
                {[2024, 2025, 2026, 2027].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Select>
            </div>
          </div>

          {/* Results Display */}
          {bulkResults && (
            <div className="mb-4 space-y-3">
              <div className="text-sm font-medium text-gray-700">Results:</div>
              
              {bulkResults.generated?.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-green-700 font-medium text-sm mb-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Generated: {bulkResults.generated.length}
                  </div>
                  <div className="text-xs text-green-600 max-h-24 overflow-y-auto">
                    {bulkResults.generated.map((r, i) => (
                      <div key={i}>{r.employee} - ₹{r.netPayable?.toLocaleString()}</div>
                    ))}
                  </div>
                </div>
              )}

              {bulkResults.emailed?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-blue-700 font-medium text-sm mb-2">
                    <Mail className="w-4 h-4" />
                    Emailed: {bulkResults.emailed.length}
                  </div>
                  <div className="text-xs text-blue-600 max-h-24 overflow-y-auto">
                    {bulkResults.emailed.map((r, i) => (
                      <div key={i}>{r.employee} → {r.email}</div>
                    ))}
                  </div>
                </div>
              )}

              {bulkResults.skipped?.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-yellow-700 font-medium text-sm mb-2">
                    <AlertCircle className="w-4 h-4" />
                    Skipped: {bulkResults.skipped.length}
                  </div>
                  <div className="text-xs text-yellow-600 max-h-24 overflow-y-auto">
                    {bulkResults.skipped.map((r, i) => (
                      <div key={i}>{r.employee} - {r.reason}</div>
                    ))}
                  </div>
                </div>
              )}

              {bulkResults.failed?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-700 font-medium text-sm mb-2">
                    <AlertCircle className="w-4 h-4" />
                    Failed: {bulkResults.failed.length}
                  </div>
                  <div className="text-xs text-red-600 max-h-24 overflow-y-auto">
                    {bulkResults.failed.map((r, i) => (
                      <div key={i}>{r.employee} - {r.reason}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => { setShowBulkModal(false); setBulkResults(null); }}
              disabled={bulkGenerating}
            >
              {bulkResults ? 'Close' : 'Cancel'}
            </Button>
            {!bulkResults && (
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={handleBulkGenerateAndEmail}
                disabled={bulkGenerating}
              >
                {bulkGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Generate & Email All
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EmployeePayrollManagement;
